/**
 * Organization Service
 *
 * Handles organization management and user organization access.
 */
import { api } from '../core/api';
import {
  Organization,
  OrganizationWithStats,
  OrganizationStats,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  DpaStatus,
} from '../types/organization';

export const organizationService = {
  /**
   * Get all organizations the current user has access to
   */
  async getMyOrganizations(): Promise<Organization[]> {
    return api.get<Organization[]>('/organizations/me');
  },

  /**
   * Get a specific organization by ID
   */
  async getOrganization(id: string): Promise<Organization> {
    return api.get<Organization>(`/organizations/${id}`);
  },

  /**
   * Create a new organization (superuser only)
   */
  async createOrganization(data: CreateOrganizationRequest): Promise<Organization> {
    return api.post<Organization>('/organizations', data);
  },

  /**
   * Update an organization (admin/owner only)
   */
  async updateOrganization(
    id: string,
    data: UpdateOrganizationRequest
  ): Promise<Organization> {
    return api.put<Organization>(`/organizations/${id}`, data);
  },

  /**
   * Get all organizations with statistics (superuser only)
   */
  async getAllOrganizations(): Promise<OrganizationWithStats[]> {
    return api.get<OrganizationWithStats[]>('/organizations');
  },

  /**
   * Get usage and content statistics for one organization (superuser only)
   */
  async getOrganizationStats(id: string): Promise<OrganizationStats> {
    return api.get<OrganizationStats>(`/organizations/${id}/stats`);
  },

  /**
   * Update AI consent for an organization (admin/owner only)
   */
  async updateAiConsent(id: string, aiConsent: boolean): Promise<Organization> {
    return api.put<Organization>(`/organizations/${id}/ai-consent?ai_consent=${aiConsent}`);
  },

  /**
   * Delete an organization (superuser only)
   * @param id Organization ID
   * @param confirm Must be true to confirm deletion
   */
  async deleteOrganization(id: string, confirm: boolean = false): Promise<void> {
    return api.delete<void>(`/organizations/${id}?confirm=${confirm}`);
  },

  /**
   * Get the DPA acceptance status for an organization (all active members)
   */
  async getDpaStatus(id: string): Promise<DpaStatus> {
    return api.get<DpaStatus>(`/organizations/${id}/dpa-status`);
  },

  /**
   * Accept the current DPA on behalf of an organization (admin/owner only).
   * Sends the version the client was shown; the server returns 409 if it is
   * no longer current.
   */
  async acceptDpa(id: string, dpaVersion: string): Promise<DpaStatus> {
    return api.post<DpaStatus>(`/organizations/${id}/dpa-acceptance`, {
      dpa_version: dpaVersion,
    });
  },
};
